import { ValidationService } from "@/domain/records/services/ValidationService";
import {
  AvailableVersions,
  Language,
  ReportType,
} from "@/domain/records/types";
import { GenerationRequest } from "@/domain/records/value-objects/GenerationRequest";

interface GenerationRequestConfig {
  reportTypes: ReportType[];
  customPrompt?: string;
  goals?: string;
  guide?: string;
  notes?: string;
  participantCount?: number;
  respondentNames: string[];
  language?: Language | string;
  reportLanguage?: Language | string;
  sttProvider?: "auto" | "assembly" | "soniox";
  isWithTranscript: boolean;
  isWithInsightReport: boolean;
}

export interface GenerationConfigSnapshot {
  reportTypes: ReportType[];
  customPrompt?: string | null;
  goals?: string | null;
}

/**
 * Domain Service for Report Generation business logic
 * Clean Architecture - Domain Layer
 */
export class GenerationDomainService {
  /**
   * Create generation request from setup config
   */
  createGenerationRequest(
    recordId: string,
    config: GenerationRequestConfig
  ): GenerationRequest {
    return new GenerationRequest(
      recordId,
      config.reportTypes,
      config.customPrompt,
      config.goals, // goals (research context)
      config.guide, // guide (interview guide)
      config.notes, // notes (additional info)
      config.participantCount,
      config.respondentNames.filter((name) => name.trim().length > 0),
      config.language,
      config.reportLanguage,
      config.isWithTranscript,
      config.isWithInsightReport,
      config.sttProvider
    );
  }

  /**
   * Validate generation request
   */
  validateRequest(request: GenerationRequest) {
    return ValidationService.validateGenerationRequest(request);
  }

  /**
   * Calculate estimated processing time based on request complexity
   */
  estimateProcessingTime(request: GenerationRequest): {
    estimatedSeconds: number;
    factors: string[];
  } {
    let baseTime = 60; // 1 minute base
    const factors: string[] = [];

    // Factor for transcript processing
    if (request.includesTranscript) {
      baseTime += 180; // +3 minutes for transcript
      factors.push("Transcript processing");
    }

    // Factor for report types
    const reportMultiplier = request.reportTypes.length;
    baseTime += (reportMultiplier - 1) * 120; // +2 minutes per additional report
    factors.push(`${request.reportTypes.length} report types`);

    // Factor for custom prompts (more complex processing)
    if (request.customPrompt && request.customPrompt.length > 1000) {
      baseTime += 60; // +1 minute for complex custom prompts
      factors.push("Complex custom prompt");
    }

    // Factor for multiple respondents
    if (request.respondentNames.length > 3) {
      baseTime += 30; // +30 seconds for many respondents
      factors.push("Multiple respondents");
    }

    return {
      estimatedSeconds: Math.max(60, Math.min(baseTime, 1800)), // 1-30 minutes range
      factors,
    };
  }

  /**
   * Get generation progress messages
   */
  getProgressMessages(): Record<string, string> {
    return {
      uploading: "Uploading file...",
      transcription: "Transcribing audio...",
      analysis: "Analyzing content...",
      qa_generation: "Generating Q&A report...",
      persona_generation: "Generating Persona report...",
      jtbd_generation: "Generating JTBD report...",
      custom_generation: "Generating Custom report...",
      general_generation: "Generating General insights...",
      finalizing: "Finalizing reports...",
      complete: "Generation complete!",
    };
  }

  /**
   * Determine generation priority based on request
   */
  getGenerationPriority(request: GenerationRequest): "low" | "normal" | "high" {
    const hasCustom = request.reportTypes.includes("Custom");
    const hasMultipleReports = request.reportTypes.length > 2;
    const hasComplexPrompt =
      request.customPrompt && request.customPrompt.length > 500;

    if (hasCustom && hasComplexPrompt) {
      return "high"; // Complex custom requests get higher priority
    }

    if (hasMultipleReports) {
      return "normal";
    }

    return "low";
  }

  /**
   * Check if regeneration is needed based on setup changes
   */
  isRegenerationNeeded(
    currentConfig: GenerationConfigSnapshot,
    newConfig: GenerationConfigSnapshot,
    availableVersions: AvailableVersions | null | undefined
  ): boolean {
    // Check if report types changed
    const currentReports = this.extractGeneratedReports(availableVersions);
    const newReports = newConfig.reportTypes || [];

    if (
      currentReports.length !== newReports.length ||
      currentReports.some((report) => !newReports.includes(report))
    ) {
      return true;
    }

    // Check if custom prompt changed for custom reports
    if (
      newReports.includes("Custom") &&
      currentConfig.customPrompt !== newConfig.customPrompt
    ) {
      return true;
    }

    // Check if research context significantly changed
    if (
      currentConfig.goals !== newConfig.goals &&
      Math.abs(
        (currentConfig.goals || "").length - (newConfig.goals || "").length
      ) > 50
    ) {
      return true;
    }

    return false;
  }

  /**
   * Extract which reports were actually generated from versions
   */
  private extractGeneratedReports(
    availableVersions: AvailableVersions | null | undefined
  ): ReportType[] {
    if (!availableVersions) {
      return [];
    }

    const reports: ReportType[] = [];

    if ((availableVersions.qa_report || []).length > 0) reports.push("Q&A");
    if ((availableVersions.general || []).length > 0) reports.push("General");
    if ((availableVersions.jtbd_markdown_report || []).length > 0)
      reports.push("JTBD");
    if ((availableVersions.persona_report || []).length > 0)
      reports.push("Persona");
    if ((availableVersions.custom_report || []).length > 0)
      reports.push("Custom");

    return reports;
  }

  /**
   * Get recommended report types based on content analysis
   */
  getRecommendedReportTypes(
    contentType: "audio" | "video" | "text"
  ): ReportType[] {
    switch (contentType) {
      case "text":
        return ["General", "Q&A", "JTBD"];
      case "audio":
      case "video":
        return ["Q&A", "General", "JTBD", "Persona"];
      default:
        return ["Q&A", "General", "JTBD"];
    }
  }
}
